/**
 * Background Refresh Service
 * Manages automatic token refresh in the background
 */

import { TokenStatusService } from "./tokenStatusService";
import { WithingsTokenService } from "./withings-token-service";

/**
 * Background Refresh Service
 * Handles proactive token refresh without user interaction
 */
let refreshInterval: NodeJS.Timeout | null = null;
let isRunning = false;

/**
 * Perform proactive token refresh if needed
 */
async function performProactiveRefresh(): Promise<void> {
  try {
    const shouldRefresh = await TokenStatusService.shouldRefreshToken();

    if (shouldRefresh) {
      await WithingsTokenService.refreshToken();
    }
  } catch (error) {
    // NOTE: Don't throw - this is a background operation
    // Reason: Background refresh failures should not disrupt the application
    // Errors are logged silently to prevent console noise
    if (error instanceof Error) {
      // Silent error handling for background operations
    }
  }
}

export const BackgroundRefreshService = {
  /**
   * Start background refresh service
   */
  start(): void {
    if (isRunning) {
      return;
    }

    // Clear any existing interval
    BackgroundRefreshService.stop();

    // Check every 5 minutes
    refreshInterval = setInterval(
      () => {
        void performProactiveRefresh();
      },
      5 * 60 * 1000,
    ); // 5 minutes

    isRunning = true;
  },

  /**
   * Stop background refresh service
   */
  stop(): void {
    if (refreshInterval) {
      clearInterval(refreshInterval);
      refreshInterval = null;
    }

    isRunning = false;
  },

  /**
   * Check if service is currently running
   *
   * @returns {boolean} True if service is running
   *
   */
  isServiceRunning(): boolean {
    return isRunning;
  },

  /**
   * Force immediate refresh check
   */
  async forceRefreshCheck(): Promise<void> {
    await performProactiveRefresh();
  },
};
